import rawConfig from '../config.json';

import {
    AssetUrlResolver,
    BlurConfig,
    BlurStrength,
    Generator,
    InputConfig,
    MaskConfig,
    PassthroughConfig,
    PipelineConfig,
    PreloadConfig,
    Quality,
    RawConfig,
    RenderConfig,
} from './config-types';
import { logger } from './logger';
import { LIB_VERSION } from './version';

/**
 * Internal mutable versions of the configurations used within the builder.
 */
interface MutableSizeConfig {
    height: number;
    width: number;
}

interface MutableRenderConfig {
    type: 'blur' | 'passthrough' | 'replacement';
    horizontalMirror?: boolean;
}

interface MutableBlurConfig extends MutableRenderConfig {
    type: 'blur';
    kernelSize: number;
    sigma: number;
}

interface MutableReplacementConfig extends MutableRenderConfig {
    type: 'replacement';
    background: HTMLCanvasElement | HTMLVideoElement;
    static: boolean;
}
interface MutableBaseMaskConfig {
    generator: Generator;
    generationIntervalMs: number;
    modelRank: number;
    modelUri: string;
    workerUri: string;
    inputSize?: MutableSizeConfig;
    outputSize?: MutableSizeConfig;
    upscaleSize?: MutableSizeConfig;
}

interface MutableMaskConfig extends MutableBaseMaskConfig {
    inputSize: MutableSizeConfig;
    outputSize: MutableSizeConfig;
    upscaleSize: MutableSizeConfig;
    qualityKey?: keyof RawConfig['modelQualities'];
}

interface MutablePipelineConfig {
    baseUrl?: string;
    wasmUri?: string;
    executionProviders?: string[];
    render?: MutableBlurConfig | MutableRenderConfig | MutableReplacementConfig;
    input?: MutableSizeConfig;
    mask?: MutableMaskConfig;
    assetUrlResolver?: AssetUrlResolver;
}

/**
 * A builder class for creating PipelineConfig instances in a flexible and intuitive way.
 */
export class ConfigurationBuilder {
    private config: MutablePipelineConfig = {};

    constructor(private env: 'int' | 'prod' = 'prod') {}

    /**
     * Sets the base URL for the configuration.
     * @param baseUrl The base URL to use.
     */
    public setBaseUrl(baseUrl: string): ConfigurationBuilder {
        // Remove trailing slashes from the base URL
        this.config.baseUrl = baseUrl.replace(/\/+$/, '');
        return this;
    }

    /**
     * Sets the WASM URI for the configuration.
     * @param wasmUri The WASM URI to use.
     */
    public setWasmUri(wasmUri: string): ConfigurationBuilder {
        this.config.wasmUri = wasmUri;
        return this;
    }

    /**
     * Sets the asset URL resolver.
     * @param assetUrlResolver The asset URL resolver to use
     * @returns The current ConfigurationBuilder instance.
     */
    public setAssetUrlResolver(assetUrlResolver: AssetUrlResolver): ConfigurationBuilder {
        this.config.assetUrlResolver = assetUrlResolver;
        return this;
    }

    /**
     * Sets the execution providers for the ONNX runtime.
     *
     * @param executionProviders The execution providers to use.
     */
    public setExecutionProviders(executionProviders: string[]): ConfigurationBuilder {
        // Allow a single string to be passed as well. This is useful for setting a single provider.
        // This will convert the string to an array for consistency.
        if (typeof executionProviders === 'string') {
            executionProviders = [executionProviders];
        }
        this.config.executionProviders = executionProviders;
        return this;
    }

    /**
     * Sets the input size for the configuration.
     * @param height The height of the input.
     * @param width The width of the input.
     */
    public setInputSize(height: number, width: number): ConfigurationBuilder {
        this.config.input = { height, width };
        return this;
    }

    /**
     * Sets the input configuration.
     * @param inputConfig The input configuration to use.
     */
    public setInputConfig(inputConfig: InputConfig): ConfigurationBuilder {
        this.config.input = { ...inputConfig };
        return this;
    }

    /**
     * Sets the render configuration.
     * @param renderConfig The render configuration to use.
     */
    public setRenderConfig(renderConfig: RenderConfig): ConfigurationBuilder {
        this.config.render = { ...renderConfig } as MutableRenderConfig;
        return this;
    }

    /**
     * Sets the render type.
     * @param type The render type ('blur', 'passthrough', 'replacement').
     */
    public setRenderType(type: 'blur' | 'passthrough' | 'replacement'): ConfigurationBuilder {
        if (!this.config.render) {
            this.config.render = { type };
        } else {
            this.config.render.type = type;
        }
        return this;
    }

    /**
     * Sets the horizontal mirror property for the render configuration.
     * @param horizontalMirror Boolean indicating if horizontal mirror should be applied.
     */
    public setRenderHorizontalMirror(horizontalMirror: boolean): ConfigurationBuilder {
        if (!this.config.render) {
            logger.errorAndThrow('Render config must be set before setting horizontal mirror.');
        }
        this.config.render.horizontalMirror = horizontalMirror;
        return this;
    }

    /**
     * Sets the blur strength for the render configuration.
     * @param blurStrength The blur strength to use.
     */
    public setBlurStrength(blurStrength: BlurStrength): ConfigurationBuilder {
        const blurConfig = ConfigurationBuilder.getBlurConfig(
            blurStrength,
            this.config.render?.horizontalMirror
        );
        this.config.render = { ...this.config.render, ...blurConfig };
        return this;
    }

    /**
     * Sets the blur kernel size.
     * @param kernelSize The kernel size for the blur effect.
     */
    public setBlurKernelSize(kernelSize: number): ConfigurationBuilder {
        if (!this.config.render || this.config.render.type !== 'blur') {
            logger.errorAndThrow('Render config must be of type blur to set blur properties.');
        }
        (this.config.render as MutableBlurConfig).kernelSize = kernelSize;
        return this;
    }

    /**
     * Sets the blur sigma value.
     * @param sigma The sigma value for the blur effect.
     */
    public setBlurSigma(sigma: number): ConfigurationBuilder {
        if (!this.config.render || this.config.render.type !== 'blur') {
            logger.errorAndThrow('Render config must be of type blur to set blur properties.');
        }
        (this.config.render as MutableBlurConfig).sigma = sigma;
        return this;
    }

    /**
     * Sets the replacement background.
     * @param background The background canvas or video element to use for replacement.
     */
    public setReplacementBackground(
        background: HTMLCanvasElement | HTMLVideoElement
    ): ConfigurationBuilder {
        if (!this.config.render || this.config.render.type !== 'replacement') {
            logger.errorAndThrow(
                'Render config must be of type replacement to set replacement properties.'
            );
        }
        (this.config.render as MutableReplacementConfig).background = background;
        return this;
    }

    /**
     * Sets the replacement static property.
     * @param isStatic Boolean indicating if the replacement is static.
     */
    public setReplacementStatic(isStatic: boolean): ConfigurationBuilder {
        if (!this.config.render || this.config.render.type !== 'replacement') {
            logger.errorAndThrow(
                'Render config must be of type replacement to set replacement properties.'
            );
        }
        (this.config.render as MutableReplacementConfig).static = isStatic;
        return this;
    }

    /**
     * Sets the mask configuration.
     * @param maskConfig The mask configuration to use.
     */
    public setMaskConfig(maskConfig: MaskConfig): ConfigurationBuilder {
        this.config.mask = { ...maskConfig } as MutableMaskConfig;
        return this;
    }

    /**
     * Sets the mask generator.
     * @param generator The generator type (most be 'worker'; 'local' is deprecated).
     */
    public setMaskGenerator(generator: Generator): ConfigurationBuilder {
        if (generator !== 'worker') {
            logger.warn('Mask generator must be "worker". "local" is deprecated.');
        }
        const mask = this.getMaskConfig();
        mask.generator = generator;
        return this;
    }

    /**
     * Sets the mask generation interval in milliseconds.
     * @param intervalMs The generation interval in milliseconds.
     */
    public setMaskGenerationIntervalMs(intervalMs: number): ConfigurationBuilder {
        const mask = this.getMaskConfig();
        mask.generationIntervalMs = intervalMs;
        return this;
    }

    /**
     * Sets the mask model rank.
     * @param modelRank The model rank to use.
     */
    public setMaskModelRank(modelRank: number): ConfigurationBuilder {
        const mask = this.getMaskConfig();
        mask.modelRank = modelRank;
        return this;
    }

    /**
     * Sets the mask model URI.
     * @param modelUri The model URI to use.
     */
    public setMaskModelUri(modelUri: string): ConfigurationBuilder {
        const mask = this.getMaskConfig();
        mask.modelUri = modelUri;
        return this;
    }

    /**
     * Sets the mask worker URI.
     * @param workerUri The worker URI to use.
     */
    public setMaskWorkerUri(workerUri: string): ConfigurationBuilder {
        const mask = this.getMaskConfig();
        mask.workerUri = workerUri;
        return this;
    }

    /**
     * Sets the mask input size.
     * @param height The height of the mask input.
     * @param width The width of the mask input.
     */
    public setMaskInputSize(height: number, width: number): ConfigurationBuilder {
        const mask = this.getMaskConfig();
        mask.inputSize = { height, width };
        return this;
    }

    /**
     * Sets the mask output size.
     * @param height The height of the mask output.
     * @param width The width of the mask output.
     */
    public setMaskOutputSize(height: number, width: number): ConfigurationBuilder {
        const mask = this.getMaskConfig();
        mask.outputSize = { height, width };
        return this;
    }

    /**
     * Sets the mask upscaled size.
     * @param height The height of the upscaled mask.
     * @param width The width of the upscaled mask.
     */
    public setMaskUpscaleSize(height: number, width: number): ConfigurationBuilder {
        const mask = this.getMaskConfig();
        mask.upscaleSize = { height, width };
        return this;
    }

    /**
     * Sets the quality level and updates mask configurations accordingly.
     * @param quality The desired quality level.
     */
    public setMaskQuality(quality: Quality): ConfigurationBuilder {
        const { modelQualities } = rawConfig;

        const mask = this.getMaskConfig();
        const qualityKey = quality.toLowerCase() as keyof RawConfig['modelQualities'];
        const qualityConfig = modelQualities[qualityKey];

        if (!qualityConfig) {
            logger.errorAndThrow(`Unknown quality option: ${quality}`);
        }

        // Store the quality key for dynamic resolution later
        mask.qualityKey = qualityKey;

        // Copy other quality-specific properties into the mask config
        Object.assign(mask, qualityConfig);

        return this;
    }

    /**
     * Builds and returns the PreloadConfig or PipelineConfig object.
     *
     * @param requireInputConfig Whether to require the input configuration to be set before building.
     * Input size is not available at preload so this needs to be optional. The default value is true.
     *
     */
    public build(requireInputConfig = true): PipelineConfig | PreloadConfig {
        const baseUrl = this.resolveBaseUrl();
        const modelUri = this.resolveModelUri();
        const wasmUri = this.resolveWasmUri();
        const workerUri = this.resolveWorkerUri();

        const input = this.config.input;
        const render = this.config.render;
        const maskConfig = this.config.mask;
        const executionProviders = this.config.executionProviders;
        const assetUrlResolver = this.config.assetUrlResolver;

        if (!maskConfig) {
            logger.errorAndThrow(
                'Mask configuration must be set before building the configuration.'
            );
        }

        const mask: MaskConfig = {
            generator: maskConfig.generator,
            generationIntervalMs: maskConfig.generationIntervalMs,
            modelRank: maskConfig.modelRank,
            inputSize: maskConfig.inputSize,
            outputSize: maskConfig.outputSize,
            upscaleSize: maskConfig.upscaleSize,
            modelUri,
            workerUri,
        };

        // Build the preload configuration, which is valid for both preload and pipeline.
        const preloadConfig = {
            assetUrlResolver,
            executionProviders,
            baseUrl,
            wasmUri,
            mask,
        };

        if (requireInputConfig) {
            if (!input) {
                logger.errorAndThrow('Input must be set before building the configuration.');
            }

            if (!render) {
                logger.errorAndThrow('Render must be set before building the configuration.');
            }

            // Build the full PipelineConfig
            const pipelineConfig = {
                ...preloadConfig,
                input,
                render,
            } as PipelineConfig;

            return pipelineConfig;
        }

        return preloadConfig as PreloadConfig;
    }

    /**
     * Returns the mask configuration, initializing it if necessary.
     */
    private getMaskConfig(): MutableMaskConfig {
        if (!this.config.mask) {
            this.config.mask = {} as MutableMaskConfig;
        }
        return this.config.mask;
    }

    /**
     * Returns the base URL for the configuration.
     */
    public resolveBaseUrl(): string {
        // TODO: Do we want to publish the base URL in the raw config? It seems like the models
        // auth service should be responsible for this, or the user should provide the base url.
        return this.config.baseUrl || rawConfig.baseUrls[this.env];
    }

    /**
     * Returns the base URL for the configuration.
     *
     * @returns The base URL for the configuration.
     */
    public resolveModelUri(): string {
        // First check if the property is explicitly set in the config, then fallback to rawConfig
        if (this.config.mask?.modelUri) {
            return this.config.mask.modelUri;
        }

        const mask = this.getMaskConfig();
        if (!mask.qualityKey) {
            logger.errorAndThrow('No quality set, cannot infer modelFile.');
        }

        return this.resolveTemplate(rawConfig.modelUrlTemplate, {
            BASE_PATH_MODELS: rawConfig.basePaths.models,
            MODEL_VERSION: rawConfig.modelVersion,
            MODEL_FILE: rawConfig.modelQualities[mask.qualityKey].modelFile,
        });
    }

    public resolveWasmUri(): string {
        // First check if the property is explicitly set in the config, then fallback to rawConfig
        if (this.config.wasmUri) {
            return this.config.wasmUri;
        }

        return this.resolveTemplate(rawConfig.wasmPathTemplate, {
            BASE_PATH_WASM: rawConfig.basePaths.wasm,
            ONNX_VERSION: rawConfig.onnxVersion,
        });
    }

    public resolveWorkerUri(): string {
        // First check if the property is explicitly set in the config, then fallback to rawConfig
        if (this.config.mask?.workerUri) {
            return this.config.mask.workerUri;
        }

        return this.resolveTemplate(rawConfig.workerUrlTemplate, {
            BASE_PATH_WORKERS: rawConfig.basePaths.workers,
            LIB_VERSION: ConfigurationBuilder.getLibVersion(),
            WORKER_FILE: rawConfig.workerFile,
        });
    }

    /**
     * Resolves a template string with the specified values.
     *
     * @param template The template string to resolve.
     * @param values The values to use for resolution.
     * @returns The resolved template string.
     */
    private resolveTemplate(template: string, values: Record<string, string>): string {
        return template.replace(/\$\{([A-Z_]+)\}/g, (_, key) => {
            if (!values[key]) {
                logger.errorAndThrow(`Missing value for template variable: ${key}`);
            }
            return values[key];
        });
    }

    /**
     * Generates a blur configuration based on the specified blur strength.
     * @param blurStrength The desired blur strength.
     * @param horizontalMirror Whether to apply horizontal mirroring.
     */
    public static getBlurConfig(
        blurStrength: BlurStrength,
        horizontalMirror?: boolean
    ): BlurConfig {
        const strengthKey = blurStrength.toLowerCase() as keyof RawConfig['blurStrengths'];
        const blurConfig = rawConfig.blurStrengths[strengthKey];

        if (!blurConfig) {
            logger.errorAndThrow(`Unknown blur strength: ${blurStrength}`);
        }

        return {
            type: 'blur',
            horizontalMirror,
            ...blurConfig,
        };
    }

    public static getPassthroughConfig(horizontalMirror?: boolean): PassthroughConfig {
        return {
            type: 'passthrough',
            horizontalMirror,
        };
    }

    /**
     * Returns the library version.
     *
     * @returns The library version.
     */
    public static getLibVersion(): string {
        return LIB_VERSION;
    }
}
